import { ChevronDownIcon, XIcon } from "lucide-react";
import React from "react";
import {
  Autocomplete,
  AutocompleteContext,
  type AutocompleteProps,
  composeRenderProps,
  ListBoxContext,
  MenuTrigger,
  type MenuTriggerProps,
  Provider,
  type Selection,
  type SelectionMode,
  useFilter,
} from "react-aria-components";

import { Button, type ButtonProps, buttonVariants } from "./button";
import { ClearPressResponder, Pressable } from "./container";
import {
  DropdownItem,
  ListBox,
  type ListBoxProps,
  MultiSelectItem,
} from "./list-box";
import { Popover, type PopoverProps } from "./popover";
import { SearchField, SearchInput } from "./search-field";
import { cn, composeTailwindRenderProps } from "./utils";

type SearchableSelectInputProps = React.ComponentProps<typeof SearchInput>;

type SearchableSelectContextValue = {
  selectedKeys: Set<React.Key>;
  clearSelection: () => void;
  closePopover: () => void;
  hasSelection: boolean;
  isOpen: boolean;
};

const SearchableSelectContext =
  React.createContext<SearchableSelectContextValue>({
    selectedKeys: new Set(),
    clearSelection: () => {},
    closePopover: () => {},
    hasSelection: false,
    isOpen: false,
  });

export type SearchableSelectProps<T extends object> = MenuTriggerProps &
  Pick<AutocompleteProps, "filter"> &
  Pick<
    ListBoxProps<T>,
    "selectionMode" | "selectedKeys" | "onSelectionChange" | "renderEmptyState"
  >;

function toSelectedKeySet(
  selectedKeys: SearchableSelectProps<object>["selectedKeys"],
) {
  if (!selectedKeys || selectedKeys === "all") return new Set<React.Key>();
  return new Set(selectedKeys);
}

export function SearchableSelect<T extends object>({
  children,
  filter,
  selectedKeys,
  onSelectionChange,
  renderEmptyState,
  selectionMode,
  isOpen: isOpenProp,
  onOpenChange,
  ...props
}: SearchableSelectProps<T>) {
  const defaultFilter = useFilter({ sensitivity: "base" });
  const selectedSet = React.useMemo(
    () => toSelectedKeySet(selectedKeys),
    [selectedKeys],
  );
  const [internalOpen, setInternalOpen] = React.useState(false);

  const isControlled = isOpenProp !== undefined;
  const isOpen = isControlled ? isOpenProp : internalOpen;
  const setOpen = React.useCallback(
    (open: boolean) => {
      if (!isControlled) {
        setInternalOpen(open);
      }
      onOpenChange?.(open);
    },
    [isControlled, onOpenChange],
  );

  const handleSelectionChange = React.useCallback(
    (keys: Selection) => {
      onSelectionChange?.(keys);
      if (selectionMode !== "multiple" && keys !== "all" && keys.size > 0) {
        setOpen(false);
      }
    },
    [onSelectionChange, selectionMode, setOpen],
  );

  const contextValue = React.useMemo(
    () => ({
      selectedKeys: selectedSet,
      hasSelection: selectedSet.size > 0,
      clearSelection: () => {
        handleSelectionChange(new Set() as Selection);
      },
      closePopover: () => {
        setOpen(false);
      },
      isOpen,
    }),
    [handleSelectionChange, selectedSet, setOpen, isOpen],
  );

  return (
    <MenuTrigger {...props} isOpen={isOpen} onOpenChange={setOpen}>
      <Provider
        values={[
          [AutocompleteContext, { filter: filter ?? defaultFilter.contains }],
          [
            ListBoxContext,
            {
              selectedKeys,
              onSelectionChange: handleSelectionChange,
              renderEmptyState,
              selectionMode,
            },
          ],
        ]}
      >
        <SearchableSelectContext.Provider value={contextValue}>
          {children}
        </SearchableSelectContext.Provider>
      </Provider>
    </MenuTrigger>
  );
}

export function SearchableSelectButton({
  hideIndicator,
  isClearable,
  onClear,
  ...props
}: ButtonProps & {
  hideIndicator?: boolean;
  isClearable?: boolean;
  onClear?: () => void;
}) {
  const { hasSelection, clearSelection, closePopover } = React.useContext(
    SearchableSelectContext,
  );
  const runClear = React.useCallback(() => {
    if (onClear) {
      onClear();
    } else {
      clearSelection();
    }
    closePopover();
  }, [onClear, clearSelection, closePopover]);

  return (
    <Button
      variant="outline"
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        "gap-2 min-w-0 max-w-full text-sm sm:max-w-[24rem]",
      )}
    >
      {composeRenderProps(props.children, (children, { isDisabled }) => (
        <>
          <span className="min-w-0 flex-1 text-left">{children}</span>
          <div className="flex items-center gap-0.5 ms-auto shrink-0">
            {isClearable && hasSelection && (
              <ClearPressResponder>
                <Pressable onPress={runClear} isDisabled={isDisabled}>
                  <span
                    tabIndex={-1}
                    role="button"
                    className={buttonVariants({
                      variant: "ghost",
                      size: "icon-sm",
                      className:
                        "text-muted-foreground group-disabled:opacity-50 px-0 w-auto [&_svg]:me-0!",
                    })}
                  >
                    <XIcon />
                  </span>
                </Pressable>
              </ClearPressResponder>
            )}
            {!hideIndicator && !(isClearable && hasSelection) && (
              <ChevronDownIcon className="size-4 text-muted-foreground me-0!" />
            )}
          </div>
        </>
      ))}
    </Button>
  );
}

export function SearchableSelectPopover({ children, ...props }: PopoverProps) {
  return (
    <Popover
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        "p-0 !max-h-96 min-w-(--trigger-width) flex flex-col overflow-hidden",
      )}
    >
      <Autocomplete>{children}</Autocomplete>
    </Popover>
  );
}

export function SearchableSelectInput(props: SearchableSelectInputProps) {
  return (
    <SearchField autoFocus className="border-b" aria-label="Search">
      <SearchInput
        placeholder="Search..."
        {...props}
        className={composeTailwindRenderProps(
          props.className,
          "border-none outline-none shadow-none rounded-none ring-0 focus-visible:ring-0 focus-visible:ring-offset-0",
        )}
      />
    </SearchField>
  );
}

export function SearchableSelectList<T extends object>(
  props: Omit<
    ListBoxProps<T>,
    "selectedKeys" | "onSelectionChange" | "selectionMode"
  >,
) {
  const { isOpen } = React.useContext(SearchableSelectContext);
  const listRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isOpen) return;

    const raf = requestAnimationFrame(() => {
      const selectedItem = listRef.current?.querySelector(
        "[aria-selected='true']",
      );
      if (selectedItem instanceof HTMLElement) {
        selectedItem.scrollIntoView({
          block: "center",
          behavior: "auto",
        });
      }
    });

    return () => {
      cancelAnimationFrame(raf);
    };
  }, [isOpen]);

  return (
    <ListBox
      escapeKeyBehavior="none"
      {...props}
      ref={listRef}
      className={composeTailwindRenderProps(
        props.className,
        "outline-hidden p-1 overflow-auto flex-1 scroll-pb-1",
      )}
    />
  );
}

interface SearchableSelectValueProps<T extends object> {
  items: T[];
  getKey: (item: T) => React.Key;
  getText: (item: T) => string;
  placeholder?: React.ReactNode;
  className?: string;
  children?: (args: {
    selectedItems: T[];
    textValue: string;
  }) => React.ReactNode;
}

export function SearchableSelectValue<T extends object>({
  items,
  getKey,
  getText,
  placeholder = "Select",
  className,
  children,
}: SearchableSelectValueProps<T>) {
  const { selectedKeys } = React.useContext(SearchableSelectContext);
  const selectedItems = items.filter((item) => selectedKeys.has(getKey(item)));
  const textValue = selectedItems.map(getText).join(", ");

  if (selectedItems.length === 0) {
    return (
      <span
        className={cn(
          "block min-w-0 max-w-full truncate text-muted-foreground/70",
          className,
        )}
      >
        {placeholder}
      </span>
    );
  }

  if (children) {
    return (
      <span className={cn("block min-w-0 max-w-full truncate", className)}>
        {children({ selectedItems, textValue })}
      </span>
    );
  }

  return (
    <span className={cn("block min-w-0 max-w-full truncate", className)}>
      {textValue}
    </span>
  );
}

export const SearchableSelectItem = DropdownItem;
export const SearchableSelectMultiItem = MultiSelectItem;

SearchableSelect.Button = SearchableSelectButton;
SearchableSelect.Popover = SearchableSelectPopover;
SearchableSelect.Input = SearchableSelectInput;
SearchableSelect.List = SearchableSelectList;
SearchableSelect.Value = SearchableSelectValue;
SearchableSelect.Item = SearchableSelectItem;
SearchableSelect.MultiItem = SearchableSelectMultiItem;

export type { SelectionMode };
